import { useEffect } from "react";
import { scrollToTop } from "../internal/scroll-to-top";
import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import { PageHelpLineContext } from "../internal/page-help-line";
import { DIALOG_ROOT_ID } from "../internal/dialog-root";
import { PortalFooter } from "./PortalFooter";
import { PortalHeader } from "./PortalHeader";
import type { PortalLayoutProps } from "../types";


/**
 * Not generic upstream in a way this adapter exposes (composed from `Box`
 * fixed to `main`) — no containment note beyond "no Material UI type
 * crosses the boundary" applies.
 *
 * ADR-20261013-1z2y (extends ADR-20261008-hsvr): the shared `PortalHeader`,
 * a single main-content landmark with an optional help line, then the shared
 * `PortalFooter`. Header and footer are internal and not exported.
 *
 * The root is a full-viewport-height flex column and `main` grows to fill
 * it, so the footer sits at the bottom of the viewport on short pages.
 * `main` is itself a flex column so a child can grow to fill it. `background`
 * paints `main` itself, bottom padding included, so a white-ground screen
 * meets the footer with no tinted band and no forced extra height.
 *
 * ADR-20261016-t3rf: `main` also carries the content column's own top
 * padding — H2's README "Global shell" names it explicitly (`32px 32px
 * 48px` desktop / `24px 16px 48px` mobile for the content column, i.e. top
 * 32/24, which this value equals: `spacing.xl`=32, `spacing.lg`=24,
 * matching the existing `sm`/`xs` breakpoint split `Container.tsx` already
 * uses for the SAME column's left-right padding). No caller anywhere set
 * it before this fix — `Container.tsx`'s own doc comment deliberately
 * excludes top/bottom padding because it is reused by non-page-content
 * callers too (e.g. this file's own help-line wrapper, which supplies its
 * own top spacing); `main` is the one place shared by every page's content
 * and nothing else, so it is where the column-level top gap belongs. This
 * was the single, measured shell cause behind part of the DEBT-20261016-h2pr
 * residual: every H2 state's built page was short by a flat top-of-column
 * gap that no component anywhere was supplying. The remaining residual
 * after this fix is recorded on that debt item, not corrected here.
 *
 * ADR-20261015-bzyr: the "Sample data" marker (rendered by `PortalHeader`,
 * not here — see that file's own doc comment). Deliberately NOT a child of
 * `main`: several states this layout hosts (e.g. a contributor deep-linked
 * to a route they cannot use) require `main` to render as empty as a
 * security/visual contract this feature's own tests assert directly
 * (`toBeEmptyDOMElement()`); a marker living inside `main` would make that
 * contract impossible to hold while the marker exists at all. The header
 * renders unconditionally for every state this layout hosts, so it is the
 * one place a cross-cutting, always-present marker can live without
 * disturbing any individual screen's own content contract.
 */
export function PortalLayout({
  children,
  helpLine,
  background,
  testId,
  businessName,
  headerVariant,
}: Readonly<PortalLayoutProps>) {
  // H2: a new page opens at the top, never where the last one was scrolled to.
  useEffect(() => {
    scrollToTop();
  }, []);
  return (
    <Box
      {...cleanProps({ "data-testid": testId })}
      sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <PortalHeader {...cleanProps({ businessName, variant: headerVariant })} />
      <Box
        component="main"
        id="main-content"
        // AC-1: a programmatic focus target for the skip link -- `main`
        // is not natively focusable, so this is what lets `SkipLink`'s
        // `.focus()` call actually move focus here rather than silently
        // no-op. `-1` keeps it out of the ordinary Tab sequence (it is
        // reachable only via the skip link or script), matching the
        // standard skip-link target pattern.
        tabIndex={-1}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          // ADR-20261102-h2fd: the page's own Container carries the padding, per H2 screen. The
          // main region positions the dialog scrim, which covers only it, as in H2.
          position: "relative",
          backgroundColor: tokens.colour.neutral.surface,
          ...(background === "white" ? { backgroundColor: tokens.colour.neutral.white } : {}),
        }}
      >
        {/* ADR-20261102-h2fd: H2 puts the help line at the foot of the page column, so the
            page's Container renders it there. */}
        {/* Dialogs mount here, first in main, as H2 places them (ADR-20261102-h2fd). */}
        <div id={DIALOG_ROOT_ID} />
        <PageHelpLineContext.Provider value={helpLine}>{children}</PageHelpLineContext.Provider>
      </Box>
      <PortalFooter />
    </Box>
  );
}
